import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, 
  Plus, 
  Eye, 
  Edit, 
  FileText, 
  Package,
  Clock,
  CheckCircle,
  Search,
  Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { chantierService } from '../../services/chantierService';
import { ChantierOrder, ChantierQuote } from '../../types';

function VentesList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [orders, setOrders] = useState<ChantierOrder[]>([]);
  const [quotes, setQuotes] = useState<ChantierQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'orders' | 'quotes'>('orders');
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });

  useEffect(() => {
    loadVentesData();
  }, [filters]);

  const loadVentesData = async () => {
    try {
      setLoading(true);
      const [ordersData, quotesData] = await Promise.all([
        chantierService.getChantierOrders(),
        chantierService.getChantierQuotes()
      ]);
      
      // Filtrer selon les permissions utilisateur
      let filteredOrders = ordersData;
      let filteredQuotes = quotesData;
      
      if (user?.role === 'charpentier') {
        // Charpentier ne voit que ses propres commandes
        filteredOrders = ordersData.filter(o => o.createdBy === user.id);
        filteredQuotes = quotesData.filter(q => q.createdBy === user.id);
      }
      
      // Appliquer les filtres de recherche
      if (filters.search) {
        const search = filters.search.toLowerCase();
        filteredOrders = filteredOrders.filter(o => 
          o.id.toLowerCase().includes(search) ||
          chantierService.getChantierName(o.chantierId).toLowerCase().includes(search)
        );
        filteredQuotes = filteredQuotes.filter(q => 
          q.id.toLowerCase().includes(search) ||
          chantierService.getChantierName(q.chantierId).toLowerCase().includes(search)
        );
      }
      
      if (filters.status) {
        filteredOrders = filteredOrders.filter(o => o.status === filters.status);
        filteredQuotes = filteredQuotes.filter(q => q.status === filters.status);
      }
      
      setOrders(filteredOrders);
      setQuotes(filteredQuotes);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les données de vente',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOrder = async (orderId: string) => {
    if (!user) return;
    
    try {
      await chantierService.validateChantierOrder(orderId, user.id);
      addNotification({
        userId: user.id,
        title: 'Commande validée',
        message: 'La sortie a été validée avec succès',
        type: 'success'
      });
      loadVentesData();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de valider la commande',
        type: 'error'
      });
    }
  };

  const canManageVentes = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';
  const canValidateOrders = canManageVentes || user?.role === 'responsable_equipe';

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'panier':
        return <ShoppingCart className="h-4 w-4" />;
      case 'a_preparer':
        return <Clock className="h-4 w-4" />;
      case 'prete':
        return <Package className="h-4 w-4" />;
      case 'sortie_validee':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-lg shadow-lg">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Ventes</h1>
            </div>
            
            {canManageVentes && (
              <div className="flex items-center space-x-3">
                <Link
                  to="/ventes/quotes/new"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <FileText className="h-4 w-4" />
                  <span>Nouveau devis</span>
                </Link>
                
                <Link
                  to="/ventes/orders/new"
                  className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Nouvelle commande</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'orders'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Commandes ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab('quotes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'quotes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Devis ({quotes.length})
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200/50">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white/80"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white/80"
            >
              <option value="">Tous les statuts</option>
              {activeTab === 'orders' ? (
                <>
                  <option value="panier">Panier</option>
                  <option value="a_preparer">À préparer</option>
                  <option value="prete">Prête</option>
                  <option value="sortie_validee">Sortie validée</option>
                </>
              ) : (
                <>
                  <option value="brouillon">Brouillon</option>
                  <option value="envoye">Envoyé</option>
                  <option value="accepte">Accepté</option>
                  <option value="refuse">Refusé</option>
                </>
              )}
            </select>

            <button
              onClick={() => setFilters({ search: '', status: '' })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-96">
          {activeTab === 'orders' ? (
            orders.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Les commandes apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commande
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Chantier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/50 divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`p-2 rounded-lg mr-3 ${
                              chantierService.getStatusColor(order.status).replace('text-', 'text-').replace('bg-', 'bg-')
                            }`}>
                              {getStatusIcon(order.status)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                #{order.id.slice(-6)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.items.length} article{order.items.length > 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {chantierService.getChantierName(order.chantierId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            chantierService.getStatusColor(order.status)
                          }`}>
                            {chantierService.getStatusLabel(order.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order.totalAmount.toFixed(2)} €
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link
                              to={`/ventes/orders/${order.id}`}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Voir les détails"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                            
                            {(order.createdBy === user?.id || canManageVentes) && order.status === 'panier' && (
                              <Link
                                to={`/ventes/orders/${order.id}/edit`}
                                className="text-gray-600 hover:text-gray-900 p-1 rounded"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                            )}
                            
                            {canValidateOrders && order.status === 'prete' && (
                              <button
                                onClick={() => handleValidateOrder(order.id)}
                                className="text-green-600 hover:text-green-900 p-1 rounded"
                                title="Valider sortie"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            quotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun devis</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Les devis apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Devis
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Chantier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Validité
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/50 divide-y divide-gray-200">
                    {quotes.map((quote) => (
                      <tr key={quote.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                #{quote.id.slice(-6)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {quote.items.length} article{quote.items.length > 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {chantierService.getChantierName(quote.chantierId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            chantierService.getStatusColor(quote.status)
                          }`}>
                            {chantierService.getStatusLabel(quote.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {quote.totalAmount.toFixed(2)} €
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(quote.validUntil).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link
                              to={`/ventes/quotes/${quote.id}`}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Voir les détails"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                            
                            {(quote.createdBy === user?.id || canManageVentes) && quote.status === 'brouillon' && (
                              <Link
                                to={`/ventes/quotes/${quote.id}/edit`}
                                className="text-gray-600 hover:text-gray-900 p-1 rounded"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default VentesList;